import { Icon } from "@screencloud/screencloud-ui-components";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import {
  Column,
  Dot,
  HeaderSubtitle,
  HeaderTextBlock,
  HeaderTitle,
} from "../CompareColumn/styles";
import {
  NoChangeContainer,
  NoChangeDescription,
  NoChangeTitle,
  SkeletonBar,
  SkeletonBody,
  SkeletonHeader,
  SkeletonList,
  SkeletonRow,
  SkeletonRowText,
  SkeletonThumbnail,
  StateContainer,
  StateDescription,
  StateTitle,
} from "./styles";

const SKELETON_ROW_COUNT = 7;
const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

interface SkeletonColumnProps {
  variant: "live" | "changes";
  testId: string;
  headerTitle: ReactNode;
  headerSubtitle?: ReactNode;
}

const SkeletonColumn = ({
  variant,
  testId,
  headerTitle,
  headerSubtitle,
}: SkeletonColumnProps) => (
  <Column data-testid={testId} $variant={variant}>
    <SkeletonHeader>
      <Dot $variant={variant} />
      <HeaderTextBlock>
        <HeaderTitle $variant={variant}>{headerTitle}</HeaderTitle>
        {headerSubtitle && <HeaderSubtitle>{headerSubtitle}</HeaderSubtitle>}
      </HeaderTextBlock>
    </SkeletonHeader>
    <SkeletonList>
      {skeletonRows.map((index) => (
        <SkeletonRow key={index} data-testid="compare-skeleton-row">
          <SkeletonThumbnail />
          <SkeletonRowText>
            <SkeletonBar $width="116px" $height="13px" />
            <SkeletonBar $width="234px" $height="11.4px" />
          </SkeletonRowText>
        </SkeletonRow>
      ))}
    </SkeletonList>
  </Column>
);

export interface CompareLoadingStateProps {
  /** "Published on {date}" node for the live column, when already known. */
  publishedOn?: ReactNode;
}

/**
 * Skeleton shown for the initial diff calculation and for realtime-triggered
 * recalculations while the modal is open (GRWBKK-1679). Keeps the two-column
 * chrome so the layout does not jump when the comparison resolves.
 */
export const CompareLoadingState = ({
  publishedOn,
}: CompareLoadingStateProps) => (
  <SkeletonBody data-testid="compare-changes-loading">
    <SkeletonColumn
      variant="live"
      testId="compare-skeleton-live"
      headerTitle={
        <FormattedMessage
          id="compare_changes.currently_live"
          defaultMessage="Currently live"
        />
      }
      headerSubtitle={publishedOn}
    />
    <SkeletonColumn
      variant="changes"
      testId="compare-skeleton-changes"
      headerTitle={
        <FormattedMessage
          id="compare_changes.changes_to_publish"
          defaultMessage="Changes to publish"
        />
      }
    />
  </SkeletonBody>
);

/**
 * Unified error body for every failure mode (preparation, realtime
 * recalculation, and publish). Icon + title + description only — the Retry
 * action lives in the modal footer (see CompareChangesModal.renderFooter),
 * matching Figma node 3480:37620.
 */
export const CompareErrorState = () => (
  <StateContainer data-testid="compare-changes-error">
    <Icon name="close" />
    <StateTitle>
      <FormattedMessage
        id="compare_changes.error.title"
        defaultMessage="Something went wrong"
      />
    </StateTitle>
    <StateDescription>
      <FormattedMessage
        id="compare_changes.error.description"
        defaultMessage="Please try again in a moment to compare the changes"
      />
    </StateDescription>
  </StateContainer>
);

export const CompareNoChangeState = () => (
  <NoChangeContainer data-testid="compare-changes-no-change">
    <Icon name="checked-circle" />
    <NoChangeTitle>
      <FormattedMessage
        id="compare_changes.no_changes.title"
        defaultMessage="There are no changes to review"
      />
    </NoChangeTitle>
    <NoChangeDescription>
      <FormattedMessage
        id="compare_changes.no_changes.description"
        defaultMessage="Someone else might have published just right now."
      />
    </NoChangeDescription>
  </NoChangeContainer>
);
